import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, FloppyDisk } from '@phosphor-icons/react';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';
import {
  TZ_OPTIONS, setUserTimezone, formatDateTime, formatStamp,
} from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Settings → 環境設定 (Preferences). Display timezone lives here. Storage stays
// UTC; only the presentation layer converts. Default is Asia/Tokyo (JST).
export default function PreferencesSettings() {
  const { user, refreshMe } = useAuth();
  const { lang } = useT(); // re-render the preview when UI language changes
  const [tz, setTz] = useState('Asia/Tokyo');
  const [saving, setSaving] = useState(false);
  const [, force] = useState(0);

  useEffect(() => {
    if (user && typeof user === 'object') setTz(user.timezone || 'Asia/Tokyo');
  }, [user]);

  // Live-preview immediately as the user picks a zone (before saving).
  const onPick = (v) => { setTz(v); setUserTimezone(v); force((n) => n + 1); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/me/preferences`, { timezone: tz });
      setUserTimezone(tz);
      await refreshMe();
      toast.success('タイムゾーンを保存しました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || '保存に失敗しました');
    } finally { setSaving(false); }
  };

  const now = new Date().toISOString();
  const dirty = user && typeof user === 'object' && (user.timezone || 'Asia/Tokyo') !== tz;

  return (
    <div className="max-w-[720px] space-y-6" data-testid="preferences-page">
      <PageHeader
        title="環境設定"
        eyebrow={<span className="inline-flex items-center gap-1.5 text-xs text-zinc-500"><Clock size={14} /> 表示タイムゾーン</span>}
        description="日時の表示に使うタイムゾーンを選びます。保存されるデータは常にUTCのままで、表示だけが選択したタイムゾーンに変換されます。"
        testid="preferences-header"
      />

      <section className="rounded-lg border border-zinc-200 bg-white p-5">
        <label htmlFor="tz-select" className="block text-sm font-medium text-zinc-800 mb-1.5">タイムゾーン</label>
        <Select value={tz} onValueChange={onPick}>
          <SelectTrigger id="tz-select" className="h-10 w-full sm:w-80 text-sm" aria-label="タイムゾーンを選ぶ" data-testid="tz-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TZ_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`tz-option-${o.value}`}>
                {o.labelJa}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="mt-4 rounded-md bg-zinc-50 border border-zinc-100 p-3" data-testid="tz-preview" data-lang={lang}>
          <p className="text-xs text-zinc-400 mb-1">現在時刻のプレビュー</p>
          <p className="text-lg font-semibold text-zinc-900 tabular-nums">{formatDateTime(now)}</p>
          <p className="text-xs text-zinc-500 mt-0.5">
            エクスポート・フッター表記: <span className="font-mono">{formatStamp(now)}</span>
          </p>
        </div>

        <div className="mt-5 flex items-center gap-2">
          <Button onClick={save} disabled={saving || !dirty}
            className="h-10 bg-teal-700 hover:bg-teal-800 text-white" data-testid="tz-save-btn">
            <FloppyDisk size={16} className="mr-1.5" /> {saving ? '保存中…' : '保存'}
          </Button>
          {dirty && <span className="text-xs text-amber-600">未保存の変更があります</span>}
        </div>
      </section>

      <p className="text-xs text-zinc-400">
        言語を切り替えても、タイムゾーンは変わりません。米国・英国など夏時間のある地域は自動で調整されます。
      </p>
    </div>
  );
}
